import { Cookie, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { inputCls, labelCls } from "./constants";

const DEFAULT_COOKIE_BODY = `## §1. Administrator danych
Administratorem plików cookie oraz danych osobowych zbieranych za pomocą strony Rmobile jest Rmobile sp. z o.o. Kontakt w sprawie ochrony danych: bok@republikamobile.pl.

## §2. Czym są pliki cookie?
Pliki cookie („ciasteczka") to niewielkie pliki tekstowe zapisywane przez przeglądarkę na Twoim urządzeniu (komputer, telefon, tablet) podczas odwiedzania stron internetowych. Zawierają zwykle nazwę strony, z której pochodzą, czas przechowywania oraz unikalny identyfikator. Cookies nie zawierają danych umożliwiających bezpośrednią identyfikację osoby, ale mogą być powiązane z innymi danymi.

## §3. Podstawa prawna
Przetwarzanie plików cookie odbywa się na podstawie: art. 173 ust. 1 Ustawy z dnia 16 lipca 2004 r. – Prawo telekomunikacyjne, art. 6 ust. 1 lit. a) RODO (zgoda użytkownika) – w zakresie cookies analitycznych i marketingowych, art. 6 ust. 1 lit. f) RODO (prawnie uzasadniony interes administratora) – w zakresie cookies niezbędnych do działania serwisu, Dyrektywy 2002/58/WE (ePrivacy) oraz projektu Rozporządzenia ePrivacy.

## §4. Kategorie plików cookie
a) Niezbędne (bez zgody) – konieczne do funkcjonowania strony, obsługi formularzy, sesji użytkownika i bezpieczeństwa. Bez nich strona nie może działać poprawnie. Przykłady: identyfikator sesji, token zabezpieczający CSRF, ustawienia zgody na cookies. Okres przechowywania: sesja / do 12 miesięcy.
b) Analityczne (wymagana zgoda) – pomagają nam analizować w jaki sposób użytkownicy korzystają ze strony: liczba wizyt, źródła ruchu, czas spędzony na stronie, najczęściej odwiedzane podstrony. Dane są zanonimizowane. Narzędzia: Google Analytics 4. Okres przechowywania: do 24 miesięcy.
c) Marketingowe / reklamowe (wymagana zgoda) – pozwalają wyświetlać spersonalizowane reklamy w serwisach zewnętrznych (Facebook, Google Ads) oraz mierzyć skuteczność kampanii. Narzędzia: Meta Pixel, Google Ads Conversion Tracking. Okres przechowywania: do 13 miesięcy.

## §5. Odbiorcy danych z cookies
Dane zebrane za pomocą cookies mogą być przekazywane następującym podmiotom: Google Ireland Limited (Google Analytics, Google Ads), Meta Platforms Ireland Limited (Meta Pixel), Stripe Payments Europe Limited (obsługa płatności), dostawcy usług IT i hostingowych działający na nasze zlecenie.
Niektóre z podmiotów mają siedzibę poza EOG (np. USA). Transfer odbywa się na podstawie standardowych klauzul umownych zatwierdzonych przez Komisję Europejską.

## §6. Twoje prawa
Prawo do wyrażenia i cofnięcia zgody w dowolnej chwili (bez wpływu na przetwarzanie sprzed cofnięcia), prawo dostępu do swoich danych oraz ich sprostowania, prawo do usunięcia danych („prawo do bycia zapomnianym"), prawo do ograniczenia przetwarzania, prawo do przenoszenia danych, prawo wniesienia sprzeciwu, prawo wniesienia skargi do Prezesa Urzędu Ochrony Danych Osobowych (uodo.gov.pl).

## §7. Jak zarządzać cookies?
Możesz w każdej chwili: zmienić swoją zgodę klikając link „Ustawienia cookies" w stopce lub czyszcząc dane strony w przeglądarce; zablokować lub usunąć pliki cookie z poziomu ustawień przeglądarki (Chrome, Firefox, Safari, Edge – każda posiada odpowiednie opcje w menu prywatności); skorzystać z trybu incognito / prywatnego przeglądania.
Wyłączenie plików cookie niezbędnych może spowodować, że niektóre funkcje strony przestaną działać.

## §8. Kontakt
W sprawach związanych z przetwarzaniem danych i plikami cookie skontaktuj się z nami: bok@republikamobile.pl.`;

export default function CookiePolicySection({ pages, setPages }) {
  const cp = pages.cookie_policy || { title: "", subtitle: "", body: "" };
  const upd = (changes) => setPages({ ...pages, cookie_policy: { ...cp, ...changes } });

  const loadTemplate = () => {
    if (cp.body && cp.body.length > 200) {
      if (!window.confirm("Aktualna treść zostanie NADPISANA pełnym szablonem. Kontynuować?")) return;
    }
    upd({
      title: cp.title || "Polityka plików cookie",
      subtitle: cp.subtitle || "Ostatnia aktualizacja: 1 stycznia 2026",
      body: DEFAULT_COOKIE_BODY,
    });
    toast.success("Wczytano pełny szablon. Pamiętaj o zapisaniu zmian.");
  };

  return (
    <section>
      <h3 className="flex items-center gap-2 font-bold text-slate-800 text-lg mb-4">
        <Cookie size={18} className="text-[#1f3a8a]" /> Polityka cookies
      </h3>
      <div className="rounded-xl border border-slate-200 p-5 bg-slate-50/50 space-y-4">
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Tytuł</label>
            <input
              data-testid="cookie-title"
              className={inputCls}
              value={cp.title}
              onChange={(e) => upd({ title: e.target.value })}
            />
          </div>
          <div>
            <label className={labelCls}>Podtytuł (np. data aktualizacji)</label>
            <input
              data-testid="cookie-subtitle"
              className={inputCls}
              value={cp.subtitle}
              onChange={(e) => upd({ subtitle: e.target.value })}
            />
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className={labelCls}>Treść polityki cookies</label>
            <button
              data-testid="cookie-load-template"
              type="button"
              onClick={loadTemplate}
              className="inline-flex items-center gap-1 text-xs font-semibold text-[#1f3a8a] hover:text-[#dc2626] px-2.5 py-1.5 rounded border border-slate-300 hover:border-[#dc2626] transition"
            >
              <RotateCcw size={12} /> Wczytaj pełny szablon (8 sekcji)
            </button>
          </div>
          <p className="text-xs text-slate-500 mb-2">
            Każda linia zaczynająca się od <code className="bg-slate-100 px-1 rounded">## </code> to tytuł sekcji.
            Pozostałe linie to zwykłe paragrafy. Puste linie oddzielają sekcje.
          </p>
          <textarea
            data-testid="cookie-body"
            className={`${inputCls} font-mono text-xs leading-relaxed`}
            rows={22}
            value={cp.body}
            onChange={(e) => upd({ body: e.target.value })}
          />
        </div>
      </div>
    </section>
  );
}
